import React from 'react';

import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStepRawData} from 'pipelines/PipelineTypes';
import {getStepDataKindForRawData} from 'pipelines/RawStepDataKindRegistry';
import {InputStepDatasRecord} from 'pipelines/StepDataUtils';

import ChartView from './ChartView';
import DataFrameView from './DataFrameView';
import FileView from './FileView';
import ChartPopoverContents from './popovers/ChartPopoverContents';
import DataFramePopoverContents from './popovers/DataFramePopoverContents';
import FilePopoverContents from './popovers/FilePopoverContents';

/** A LAYOUT step input slot name (the kinds of data a layout view can hold). */
export type LayoutInputDataName = keyof InputStepDatasRecord<PipelineStepType.LAYOUT, 0>;

/**
 * Analysis-tool-level registry of the per-raw-data-kind UI: the full data view,
 * the hover-popover preview, and which LAYOUT input slot the kind routes to.
 *
 * This lives in `analysis-tool` (rather than the core `pipelines`
 * RawStepDataKindRegistry) because the components depend on analysis-tool /
 * data-view infrastructure, which the lower-level `pipelines` layer cannot
 * import. Consumers (DataView, RawDataPopover, LayoutViewUtils) read from this
 * single map instead of branching per kind, so adding a kind's UI is one entry.
 *
 * The map is keyed by kind, so a newly added kind simply has no entry (and the
 * consumer falls back gracefully) until its components are registered.
 */
interface RawDataViewEntry {
  /** The full view component for this kind. Resolves its own data from context. */
  View: () => JSX.Element;
  /** Renders the hover-popover preview for a raw data of this kind. */
  renderPopoverContents: (rawData: PipelineStepRawData) => JSX.Element | null;
  /**
   * The LAYOUT step input slot a view of this kind routes to. Omitted for kinds
   * that cannot be placed in a layout (the LAYOUT step has no slot for them).
   */
  layoutInputName?: LayoutInputDataName;
}

const RAW_DATA_VIEW_REGISTRY: Partial<Record<PipelineStepDataKind, RawDataViewEntry>> = {
  [PipelineStepDataKind.CHART]: {
    View: ChartView,
    renderPopoverContents: (rawData) => (
      <ChartPopoverContents rawData={rawData as PipelineStepRawData<PipelineStepDataKind.CHART>} />
    ),
    layoutInputName: 'Input charts',
  },
  [PipelineStepDataKind.DATASET]: {
    View: DataFrameView,
    renderPopoverContents: (rawData) => (
      <DataFramePopoverContents rawData={rawData as PipelineStepRawData<PipelineStepDataKind.DATASET>} />
    ),
    layoutInputName: 'Input datasets',
  },
  [PipelineStepDataKind.FILE]: {
    View: FileView,
    renderPopoverContents: (rawData) => (
      <FilePopoverContents rawData={rawData as PipelineStepRawData<PipelineStepDataKind.FILE>} />
    ),
    layoutInputName: 'Input files',
  },
};

/** Return the view component registered for a kind, or `undefined` if none. */
export function getViewForStepDataKind(kind: PipelineStepDataKind): (() => JSX.Element) | undefined {
  return RAW_DATA_VIEW_REGISTRY[kind]?.View;
}

/**
 * Render the popover preview for a raw step data, or `null` if its kind has no
 * registered popover content.
 */
export function renderRawDataPopoverContents(rawData: PipelineStepRawData): JSX.Element | null {
  const kind = getStepDataKindForRawData(rawData);
  const entry = kind != null ? RAW_DATA_VIEW_REGISTRY[kind] : undefined;
  return entry != null ? entry.renderPopoverContents(rawData) : null;
}

/**
 * Return the LAYOUT input slot a view of this kind routes to, or `undefined` if
 * the kind is not registered.
 */
export function getLayoutInputNameForStepDataKind(
  kind: PipelineStepDataKind
): LayoutInputDataName | undefined {
  return RAW_DATA_VIEW_REGISTRY[kind]?.layoutInputName;
}
